import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography
} from "@mui/material";
import "../admin/Admin.css";
import {Room} from "../../data/rooms";
import {editRoom} from "../../services/scheduleService";

interface EditRoomModalProps {
    isOpen: boolean;
    onClose: () => void;
    allRooms: Room[]; // List of all rooms to choose from
    refreshRooms: () => void;
}

const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const EditRoomModal: React.FC<EditRoomModalProps> = ({
                                                         isOpen,
                                                         onClose,
                                                         allRooms,
                                                         refreshRooms,
                                                     }) => {
    const [selectedRoomId, setSelectedRoomId] = useState<string>("");
    const [roomData, setRoomData] = useState<Room | null>(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState({
        Title: '',
        Date: '',
        Description: '',
    });

    useEffect(() => {
        if (isOpen) {
            setSelectedRoomId("");
            setRoomData(null);
            setErrors({Title: "", Date: "", Description: ""});
            setErrorMessage(null);
        }
    }, [isOpen]);

    useEffect(() => {
        const found = allRooms.find((r) => r.RoomId === selectedRoomId);
        setRoomData(found || null);
    }, [selectedRoomId, allRooms]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (!roomData) return;

        const {name, type, value} = e.target;
        const newValue = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

        setRoomData({...roomData, [name]: newValue});
        setErrors({...errors, [name]: ""});
    };

    const validateFields = () => {
        let valid = true;
        const newErrors = {Title: "", Date: "", Description: ""};

        if (!roomData?.Title.trim()) {
            newErrors.Title = "Title is required.";
            valid = false;
        }

        if (!roomData?.Description.trim()) {
            newErrors.Description = "Description is required.";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!roomData || !validateFields()) return;

        setLoading(true);
        setErrorMessage(null);

        try {
            await editRoom(roomData);

            refreshRooms();
            onClose();
        } catch (error) {
            console.error("Edit Room Error:", error);

            const fallback = "Failed to update room. Please try again.";
            const message = error instanceof Error ? error.message : fallback;
            setErrorMessage(message);

        } finally {
            setLoading(false);
        }
    };


    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="edit-room-modal">
            <Box sx={modalStyle}>
                <Typography variant="h6" component="h2">
                    Edit Room
                </Typography>
                <FormControl fullWidth margin="dense">
                    <InputLabel>Select Room</InputLabel>
                    <Select
                        value={selectedRoomId}
                        onChange={(e) => setSelectedRoomId(e.target.value)}
                        label="Select Room"
                    >
                        <MenuItem value="" disabled>
                            -- Select a Room --
                        </MenuItem>
                        {allRooms.map((room) => (
                            <MenuItem key={room.RoomId} value={room.RoomId}>
                                {room.Title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>


                {roomData && (
                    <Box sx={{mt: 2}}>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Title"
                            name="Title"
                            value={roomData.Title}
                            onChange={handleChange}
                            error={Boolean(errors.Title)}
                            helperText={errors.Title}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Date"
                            name="Date"
                            type="date"
                            value={roomData.Date || ''}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(errors.Date)}
                            helperText={errors.Date}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Description"
                            name="Description"
                            multiline
                            rows={3}
                            value={roomData.Description}
                            onChange={handleChange}
                            error={Boolean(errors.Description)}
                            helperText={errors.Description}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="ShowDescription"
                                    checked={roomData.ShowDescription}
                                    onChange={handleChange}
                                />
                            }
                            label="Show Description"
                        />
                        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                    </Box>
                )}

                <Box sx={{mt: 3, display: "flex", justifyContent: "flex-end"}}>
                    <Button onClick={onClose} sx={{mr: 1}}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        className="editOrAdd"
                        disabled={loading || !roomData}
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditRoomModal;
